// src/App.js
import { Route, Routes, Navigate, NavLink, useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
// eslint-disable-next-line no-unused-vars
import { FaHome, FaBuilding, FaChartLine, FaFileUpload, FaTachometerAlt, FaSignInAlt, FaSignOutAlt, FaBars, FaTimes, FaList, FaClipboardCheck, FaPaperPlane } from 'react-icons/fa';
import LandingPage from './components/LandingPage';
import MapView from './components/MapView';
import PropertyForm from './components/PropertyForm';
import MarketInsights from './components/MarketInsights';
import LoginForm from './components/LoginForm';
import AdminDashboard from './components/AdminDashboard';
import MobileMenu from './components/MobileMenu';
import ExportDashboard from './components/ExportDashboard';
import MyProperties from './components/MyProperties';                    // ✅ NEW
import TeamLeadDashboard from './components/TeamLeadDashboard';          // ✅ NEW
import ClientSendPage from './components/ClientSendPage';                // ✅ NEW
import { TbFileExport } from 'react-icons/tb';
import { MdAdminPanelSettings } from 'react-icons/md';
import PropertyView from './components/PropertyView';


// ✅ Helper: read all roles from localStorage
const getUserRoles = () => {
  try {
    const rolesJson = localStorage.getItem('roles');
    if (rolesJson) {
      const parsed = JSON.parse(rolesJson);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.map(r => r.role);
      }
    }
  } catch (e) {}
  const legacyRole = localStorage.getItem('role');
  return legacyRole ? [legacyRole] : [];
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const token = localStorage.getItem('token');
  const userRoles = getUserRoles();
  console.log('ProtectedRoute: token=', token, 'roles=', userRoles);
  if (!token || userRoles.length === 0) {
    console.log('Redirecting to /login: No token or role');
    return <Navigate to="/login" />;
  }
  if (allowedRoles && !allowedRoles.some(role => userRoles.includes(role))) {
    console.log(`Redirecting to /properties: roles ${userRoles} not allowed for ${allowedRoles}`);
    return <Navigate to="/properties" />;
  }
  return children;
};

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem('token');
  const userRoles = getUserRoles();
  const userRole = userRoles[0] || 'client';
  const hasRole = (role) => userRoles.includes(role);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    navigate('/login');
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const isLandingPage = location.pathname === '/';

  // ✅ Role-based visibility helper
  const canAccess = (roles) => roles.some(r => userRoles.includes(r));
  const isTeamLeadOrAdmin = canAccess(['admin', 'team_lead']);
  const isSubmitterRole = canAccess(['team_lead', 'rep', 'broker']);
  const isWorkingRole = canAccess(['admin', 'team_lead', 'rep', 'broker']);

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {!isLandingPage && (
        <>
          <nav
            className={`bg-white text-teal-600 w-14 shadow-sm fixed h-screen transition-transform duration-300 ${
              isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
            } md:translate-x-0 z-20`}
          >
            <div className="flex items-center justify-center h-14">
              <img 
                src="/logo-mark.png" 
                alt="Treta" 
                className="w-9 h-9 object-contain"
              />
            </div>
            <ul className="space-y-1">
              {token ? (
                <>
                  {/* Home */}
                  <li className="group relative">
                    <NavLink
                      to="/"
                      className={({ isActive }) =>
                        isActive ? 'bg-teal-100 text-teal-800 flex justify-center p-2 rounded' : 'hover:bg-teal-100 flex justify-center p-2 rounded'
                      }
                      onClick={() => setIsSidebarOpen(false)}
                      aria-label="Home"
                    >
                      <FaHome size={20} />
                      <span className="absolute left-14 top-1/2 -translate-y-1/2 bg-teal-600 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">
                        Home
                      </span>
                    </NavLink>
                  </li>

                  {/* Properties */}
                  <li className="group relative">
                    <NavLink
                      to="/properties"
                      className={({ isActive }) =>
                        isActive ? 'bg-teal-100 text-teal-800 flex justify-center p-2 rounded' : 'hover:bg-teal-100 flex justify-center p-2 rounded'
                      }
                      onClick={() => setIsSidebarOpen(false)}
                      aria-label="Properties"
                    >
                      <FaBuilding size={20} />
                      <span className="absolute left-14 top-1/2 -translate-y-1/2 bg-teal-600 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">
                        Properties
                      </span>
                    </NavLink>
                  </li>

                  {/* Insights */}
                  <li className="group relative">
                    <NavLink
                      to="/insights"
                      className={({ isActive }) =>
                        isActive ? 'bg-teal-100 text-teal-800 flex justify-center p-2 rounded' : 'hover:bg-teal-100 flex justify-center p-2 rounded'
                      }
                      onClick={() => setIsSidebarOpen(false)}
                      aria-label="Insights"
                    >
                      <FaChartLine size={20} />
                      <span className="absolute left-14 top-1/2 -translate-y-1/2 bg-teal-600 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">
                        Insights
                      </span>
                    </NavLink>
                  </li>

                  {/* ✅ NEW: My Properties (admin, team_lead, rep, broker) */}
                  {isWorkingRole && (
                    <li className="group relative">
                      <NavLink
                        to="/my-properties"
                        className={({ isActive }) =>
                          isActive ? 'bg-teal-100 text-teal-800 flex justify-center p-2 rounded' : 'hover:bg-teal-100 flex justify-center p-2 rounded'
                        }
                        onClick={() => setIsSidebarOpen(false)}
                        aria-label="My Properties"
                      >
                        <FaList size={20} />
                        <span className="absolute left-14 top-1/2 -translate-y-1/2 bg-teal-600 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">
                          My Properties
                        </span>
                      </NavLink>
                    </li>
                  )}

                  {/* ✅ NEW: Approvals (admin, team_lead only) */}
                  {isTeamLeadOrAdmin && (
                    <li className="group relative">
                      <NavLink
                        to="/team-lead-dashboard"
                        className={({ isActive }) =>
                          isActive ? 'bg-teal-100 text-teal-800 flex justify-center p-2 rounded' : 'hover:bg-teal-100 flex justify-center p-2 rounded'
                        }
                        onClick={() => setIsSidebarOpen(false)}
                        aria-label="Approvals"
                      >
                        <FaClipboardCheck size={20} />
                        <span className="absolute left-14 top-1/2 -translate-y-1/2 bg-teal-600 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">
                          Approvals
                        </span>
                      </NavLink>
                    </li>
                  )}

                  {/* ✅ NEW: Client Send (admin, team_lead, rep, broker) */}
                  {isWorkingRole && (
                    <li className="group relative">
                      <NavLink
                        to="/client-send"
                        className={({ isActive }) =>
                          isActive ? 'bg-teal-100 text-teal-800 flex justify-center p-2 rounded' : 'hover:bg-teal-100 flex justify-center p-2 rounded'
                        }
                        onClick={() => setIsSidebarOpen(false)}
                        aria-label="Client Send"
                      >
                        <FaPaperPlane size={20} />
                        <span className="absolute left-14 top-1/2 -translate-y-1/2 bg-teal-600 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">
                          Client Send
                        </span>
                      </NavLink>
                    </li>
                  )}

                  {/* Export */}
                  {isWorkingRole && (
                    <li className="group relative">
                      <NavLink
                        to="/export"
                        className={({ isActive }) =>
                          isActive ? 'bg-teal-100 text-teal-800 flex justify-center p-2 rounded' : 'hover:bg-teal-100 flex justify-center p-2 rounded'
                        }
                        onClick={() => setIsSidebarOpen(false)}
                        aria-label="Export"
                      >
                        <TbFileExport size={22} />
                      </NavLink>
                      <span className="absolute left-14 top-1/2 -translate-y-1/2 bg-gray-800 text-white text-sm px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-30 pointer-events-none">
                        Export
                      </span>
                    </li>
                  )}

                  {/* Submit Property */}
                  {isSubmitterRole && (
                    <li className="group relative">
                      <NavLink
                        to="/submit-property"
                        className={({ isActive }) =>
                          isActive ? 'bg-teal-100 text-teal-800 flex justify-center p-2 rounded' : 'hover:bg-teal-100 flex justify-center p-2 rounded'
                        }
                        onClick={() => setIsSidebarOpen(false)}
                        aria-label="Submit Property"
                      >
                        <FaFileUpload size={20} />
                        <span className="absolute left-14 top-1/2 -translate-y-1/2 bg-teal-600 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">
                          Submit Property
                        </span>
                      </NavLink>
                    </li>
                  )}

                  {/* Admin Dashboard */}
                  {userRole === 'admin' && (
                    <li className="group relative">
                      <NavLink
                        to="/admin-dashboard"
                        className={({ isActive }) =>
                          isActive ? 'bg-teal-100 text-teal-800 flex justify-center p-2 rounded' : 'hover:bg-teal-100 flex justify-center p-2 rounded'
                        }
                        onClick={() => setIsSidebarOpen(false)}
                        aria-label="Admin"
                      >
                        <MdAdminPanelSettings size={20} />
                      </NavLink>
                      <span className="absolute left-14 top-1/2 -translate-y-1/2 bg-gray-800 text-white text-sm px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-30 pointer-events-none">
                        Admin
                      </span>
                    </li>
                  )}

                  {/* Logout */}
                  <li className="group relative">
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsSidebarOpen(false);
                      }}
                      className="text-teal-600 hover:bg-teal-100 flex justify-center p-2 rounded w-full"
                      aria-label="Logout"
                    >
                      <FaSignOutAlt size={20} />
                      <span className="absolute left-14 top-1/2 -translate-y-1/2 bg-teal-600 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">
                        Logout
                      </span>
                    </button>
                  </li>
                </>
              ) : (
                <li className="group relative">
                  <NavLink
                    to="/login"
                    className={({ isActive }) =>
                      isActive ? 'bg-teal-100 text-teal-800 flex justify-center p-2 rounded' : 'hover:bg-teal-100 flex justify-center p-2 rounded'
                    }
                    onClick={() => setIsSidebarOpen(false)}
                    aria-label="Login"
                  >
                    <FaSignInAlt size={20} />
                    <span className="absolute left-14 top-1/2 -translate-y-1/2 bg-teal-600 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">
                      Login
                    </span>
                  </NavLink>
                </li>
              )}
            </ul>
          </nav>

          <MobileMenu />

          <button
            className="md:hidden fixed top-2 left-2 z-30 text-teal-600 bg-white p-1 rounded shadow-sm"
            onClick={toggleSidebar}
            aria-label="Toggle sidebar"
          >
            {isSidebarOpen ? <FaTimes size={16} /> : <FaBars size={16} />}
          </button>
        </>
      )}

      <div className={`flex-1 p-2 ${!isLandingPage ? 'md:ml-14' : ''}`}>
        <Routes>
          <Route path="/login" element={<LoginForm />} />
          <Route path="/" element={<LandingPage />} />
          <Route
            path="/properties"
            element={
              <ProtectedRoute>
                <MapView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/insights"
            element={
              <ProtectedRoute>
                <MarketInsights />
              </ProtectedRoute>
            }
          />
          <Route
            path="/submit-property"
            element={
              <ProtectedRoute allowedRoles={['team_lead', 'rep', 'broker']}>
                <PropertyForm />
              </ProtectedRoute>
            }
          />

          {/* ✅ NEW: Edit Property route */}
          <Route
              path="/edit-property/:id"
              element={
                  <ProtectedRoute allowedRoles={['admin', 'team_lead', 'rep', 'broker']}>
                      <PropertyForm />
                  </ProtectedRoute>
              }
          />
          <Route
              path="/property/:id"
              element={
                  <ProtectedRoute>
                      <PropertyView />
                  </ProtectedRoute>
              }
          />
          <Route
            path="/export"
            element={
              <ProtectedRoute allowedRoles={['admin', 'team_lead', 'rep', 'broker']}>
                <ExportDashboard />
              </ProtectedRoute>
            }
          />

          {/* ✅ NEW ROUTES */}
          <Route
            path="/my-properties"
            element={
              <ProtectedRoute allowedRoles={['admin', 'team_lead', 'rep', 'broker']}>
                <MyProperties />
              </ProtectedRoute>
            }
          />

          <Route
            path="/team-lead-dashboard"
            element={
              <ProtectedRoute allowedRoles={['admin', 'team_lead']}>
                <TeamLeadDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/client-send"
            element={
              <ProtectedRoute allowedRoles={['admin', 'team_lead', 'rep', 'broker']}>
                <ClientSendPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin-dashboard"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      </div>
    </div>
  );
}

export default App;